import { trace } from '@opentelemetry/api';
import { useCookies } from '@magalu/mixer-utils';
import { parsePlaceholders } from '@magalu/mixer-structure';
import getConfig from 'next/config';
import { getToken } from '@magalu/mixer-graphql';
import { useData, useStructure } from '../../hooks';
import logger from '../../utils/logger';
import setCors from '../../utils/setCors';
import foundCheck from '../../utils/foundCheck';
import foundError from '../../utils/foundError';
import setErrorStatus from '../../utils/setErrorStatus';
import isNotFound from '../../utils/isNotFound';
import removeFiltersAndRedirect from '../../utils/removeFiltersAndRedirect';
import redirectCanonicalUrl from '../../utils/redirectCanonicalUrl';
import redirectFromRules from '../../utils/redirectFromRules';
import redirectToParent from '../../utils/redirectToParent';
import redirectFromQuery from '../../utils/redirectFromQuery';
import redirectFromEmpty from '../../utils/redirectFromEmpty';
import fetchExternalHtml from '../../utils/fetchExternalHtml';

const handleRedirects = (ctx, structure, data) => {
  const inventory = [
    redirectFromRules(logger),
    redirectFromQuery(logger),
    redirectCanonicalUrl(logger),
    redirectFromEmpty(logger),
  ];

  return inventory.reduce((acc, fn) => {
    const result = fn({ ctx, data, structure });
    if (result && !Object.keys(acc).length) {
      return { ...acc, ...result };
    }
    return acc;
  }, {});
};

const fetchPromises = (configToFetch, appConfig) => {
  return configToFetch.map(async item => {
    if (!item || !item.name || !item.url) {
      logger.warn(
        `[getServerSideProps] Invalid item in externalHTMLToFetch: ${JSON.stringify(
          item
        )}`
      );
      return { [(item && item.name) || `invalid_item_${Date.now()}`]: null };
    }
    const url = parsePlaceholders(item.url, {
      config: { ...appConfig.publicRuntimeConfig },
    });
    const fetchedData = await fetchExternalHtml({ url });

    return { [item.name]: fetchedData ? fetchedData.html : null };
  });
};

async function fetchAllExternalHtmls(structure, config) {
  let externalHtmls = {};
  let configToFetch = [];

  if (Array.isArray(structure.externalHTMLToFetch)) {
    configToFetch = structure.externalHTMLToFetch;
  } else if (Array.isArray(structure.template?.externalHTMLToFetch)) {
    configToFetch = structure.template.externalHTMLToFetch;
  }

  if (configToFetch.length === 0) {
    return externalHtmls;
  }

  const fetchedPromises = fetchPromises(configToFetch, config);

  try {
    const resultsArray = await Promise.allSettled(fetchedPromises);

    externalHtmls = resultsArray.reduce((acc, current, index) => {
      if (current?.status === 'fulfilled' && typeof current?.value === 'object') {
        return { ...acc, ...current?.value };
      }
      logger.error(
        `ERROR: fetchAllExternalHtmls - Promise rejected or with unexpected value: ${
          current.status === 'rejected' ? current.reason : current.value
        }`
      );
      const configItem = configToFetch[index];
      const itemNameForError = configItem?.name || `unknown_item_at_index_${index}`;
      acc[itemNameForError] = null;

      return acc;
    }, {});
  } catch (error) {
    logger.error(
      'ERROR: fetchAllExternalHtmls - Error during Promise.allSettled or reduce:',
      error
    );
    return {};
  }

  return externalHtmls;
}

function hasInvalidStructure(structure) {
  return !structure || !structure.site || !structure.theme || !structure.components;
}

async function handleNotFoundLogicInternal(ctx, structure, span) {
  if (structure.notfoundFallbackUrl) {
    return redirectToParent(ctx, structure);
  }
  const hasFilter = structure?.route?.filters?.filter(filter => filter.origin !== 'url')
    ?.length;

  if (hasFilter) {
    span?.addEvent('mixer.filters.not.found', {
      'log.message': `${ctx.query.filters}`,
      'log.severity': 'error',
    });
    return removeFiltersAndRedirect(ctx, structure);
  }

  return {
    newCtx: {
      ...ctx,
      query: {
        ...ctx.query,
      },
      routeId: '404',
    },
    retryAs404: true,
  };
}

async function getServerSidePropsCore(ctx) {
  const span = trace.getActiveSpan();
  try {
    const structure = await useStructure(ctx);

    if (hasInvalidStructure(structure)) {
      span?.addEvent('mixer.not.found.page.structure', {
        'log.message': 'Page cannot be loaded due to structure configs not provided',
        'log.severity': 'error',
      });
      return setErrorStatus(ctx, 404, 'This page could not be found');
    }

    if (isNotFound(structure) && ctx?.res) {
      ctx.res.statusCode = 404;
    }

    setCors(ctx);

    const cookies = useCookies(ctx, { logger });
    const { token } = await getToken(structure.site);
    const { data, errors } = await useData({
      structure: { ...structure, cookies },
      token,
    });

    const config = getConfig();

    if (data?.search?.trackId) {
      const { trackId } = data.search;
      const cookieDomain = config?.publicRuntimeConfig?.cookieDomain;
      const expires = new Date();
      expires.setFullYear(expires.getFullYear() + 1);
      ctx.res.setHeader('Set-Cookie', [
        `ml_tid=${trackId}; Domain=${
          cookieDomain[structure?.site]
        }; Expires=${expires}; path=/;`,
      ]);
    }

    foundError(structure, errors);

    const redirect = await handleRedirects(ctx, structure, data);
    if (Object.keys(redirect).length) {
      return redirect;
    }

    const found = foundCheck(structure, data);
    if (!found) {
      const notFoundAction = await handleNotFoundLogicInternal(ctx, structure, span);
      if (notFoundAction.retryAs404 && notFoundAction.newCtx) {
        return getServerSidePropsCore(notFoundAction.newCtx);
      }
      return notFoundAction;
    }

    const externalHtmls = await fetchAllExternalHtmls(structure, config);

    const props = { data: { ...data, externalHtmls }, structure };

    if (structure.ssr) {
      props.structure.cookies = cookies;
      if (token) {
        props.token = token;
      }
    }

    return { props };
  } catch (e) {
    span?.addEvent('mixer.render.error.status.500', {
      'log.message': `Error rendering page on server side: ${e.message}`,
      'log.severity': 'error',
    });
    logger.error(`[Core] Error rendering a page in url: ${ctx?.resolvedUrl}`, e);
    return setErrorStatus(ctx, 500, e.message);
  }
}

async function getServerSideProps(ctx) {
  return getServerSidePropsCore(ctx);
}

export default getServerSideProps;
